import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SitesService } from 'src/app/sites/sites.service';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { CommonService } from 'src/app/shared/services/common.service';
import { ExcelService } from 'src/app/shared/excel/excel.service';

@Component({
	selector: 'app-iotemeter-site-daily-reports',
	templateUrl: './iotemeter-site-daily-reports.component.html',
})

export class IotemeterSiteDailyReportsComponent implements OnInit, OnDestroy {

	readonly allDevicesId = 'All-Devices';

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();
	getIotemeterDailyEnergyReportsSubscription: Subscription = new Subscription();

	maxDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	toDate = this.maxDate; // yesterday
	fromDate = new Date((new Date()).getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
	minDate = this.fromDate;

	selectedDeviceType: string | number = this.allDevicesId;
	readonly devicesList = [
		{ label: this.translate.instant('iotemeter_site_energy_report.all_meters'), value: this.allDevicesId },
		{ label: this.translate.instant('iotemeter_site_energy_report.edge'), value: DeviceFactory.types.ioTEmeterEdge },
		{ label: this.translate.instant('iotemeter_site_energy_report.pro'), value: DeviceFactory.types.ioTEmeter },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp3'), value: DeviceFactory.types.ioTEmeterSp3 },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp1'), value: DeviceFactory.types.ioTEmeterSp1 },
	];

	columnConfig: ColumnsConfig[] = [
		{ key: 'date', name: this.translate.instant('g.date'), type: 'string' },
		{ key: 'count', name: this.translate.instant('iotemeter_site_energy_report.total_number_of_meters'), type: 'number' },
		{ key: 'consumed_energy', name: this.translate.instant('iotemeter_site_energy_report.total_daily_kwh_consumed'), type: 'number' },
		{ key: 'peak_energy_demand', name: this.translate.instant('iotemeter_site_energy_report.daily_peak_kw'), type: 'number' },
	];

	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		hideNoData: true,
		draggable: true,
		fileName: this.translate.instant('iotemeter_site_energy_report.daily_consumption_report'),
		csvFileHeaderInfo: [],
	};

	chartsData = {
		consumed_energy: [],
		demand_energy: [],
	};

	chartColumns = {
		consumed_energy: ['', this.translate.instant('iotemeter_site_energy_report.consumption'), { role: 'tooltip', type: 'string', p: { html: false } }],
		demand_energy: ['', this.translate.instant('iotemeter_site_energy_report.peak_demand'), { role: 'tooltip', type: 'string', p: { html: false } }],
	};

	consumedChartConfig: any = {
		title: '',
		legend: { position: 'top' },
		bar: { groupWidth: 30 },
		tooltip: { isHtml: true },
		vAxis: { title: `${this.translate.instant('g.daily')} ${this.translate.instant('iotemeter_site_energy_report.consumption')}`, format: '#\' kWh\'', }
	};

	demandChartConfig: any = {
		title: '',
		legend: { position: 'top' },
		bar: { groupWidth: 30 },
		tooltip: { isHtml: true },
		vAxis: { title: `${this.translate.instant('g.daily')} ${this.translate.instant('iotemeter_site_energy_report.peak_demand')}`, format: '#\' kW\'', }
	};

	constructor(
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private sitesService: SitesService,
		private commonService: CommonService,
		private excelService: ExcelService,
	) { }

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.currentSite = siteInfo;
				this.minDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();

				this.getIotemeterDailyEnergyReports();
			}
		});
	}

	changeDate(field, value) {
		this[field] = value;

		this.getIotemeterDailyEnergyReports();
	}

	getIotemeterDailyEnergyReports() {
		const zoneDiff = new Date().getTimezoneOffset() * -1;

		let fromDate: any = new Date(new Date(this.fromDate).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(this.toDate).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		const criteria: any = {
			siteId: this.currentSite.id,
			deviceType: this.selectedDeviceType,
			from: fromDate,
			to: toDate,
		};

		this.getIotemeterDailyEnergyReportsSubscription = this.sitesService.getIotemeterDailyEnergyReports(criteria).subscribe((response) => {
			this.prepareTableData(response);
			this.prepareChartData(response);
		});
	}

	prepareTableData(responseData: any) {
		const tableData = [];

		for (const day in responseData) {
			const rowValue = {
				date: { value: moment.unix(+day).format('MM/DD/YYYY') },
				count: { value: responseData[day].count || 0 },
				consumed_energy: { value: Math.round(responseData[day].consumed_energy) || 0 },
				peak_energy_demand: { value: Math.round(responseData[day].peak_energy_demand) || 0 },
			};

			tableData.push(rowValue);
		}

		this.tableData = tableData;

		this.tableConfig.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			includeSpacing: false
		});
	}

	prepareChartData(responseData: any) {
		const consumedChartData = [];
		const demandChartData = [];

		for (const day in responseData) {
			const consumptionRowValue = [
				new Date(+day * 1000),
				Math.round(responseData[day].consumed_energy),
				`Period: ${new Date(+day * 1000).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric'
				})}, Value: ${Math.round(responseData[day].consumed_energy)} kWh`,
			]

			const demandRowValue = [
				new Date(+day * 1000),
				Math.round(responseData[day].peak_energy_demand),
				`Period: ${new Date(+day * 1000).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric'
				})}, Value: ${Math.round(responseData[day].peak_energy_demand)} kW`,
			]

			consumedChartData.push(consumptionRowValue);
			demandChartData.push(demandRowValue);
		}

		this.chartsData.consumed_energy = consumedChartData;
		this.chartsData.demand_energy = demandChartData;

		this.setChartConfig();
	}

	setChartConfig() {
		const formDate = moment(this.fromDate).format('MM/DD/YYYY');
		const toDate = moment(this.toDate).format('MM/DD/YYYY');

		this.consumedChartConfig.title = `${this.translate.instant('iotemeter_site_energy_report.daily_energy_consumption')} ${formDate} - ${toDate}`;
		this.demandChartConfig.title = `${this.translate.instant('iotemeter_site_energy_report.daily_peak_demand')} ${formDate} - ${toDate}`;
	}

	exportDataToCsv() {
		const baseHeaders = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			includeSpacing: false
		});

		const consumedHeaders = [...baseHeaders, ['', ''], ['', ''], [this.translate.instant('g.date'), this.translate.instant('iotemeter_site_energy_report.kwh_consumed')]];
		const demandHeaders = [...baseHeaders, ['', ''], ['', ''], [this.translate.instant('g.date'), this.translate.instant('iotemeter_site_energy_report.peak_kw')]];

		this.excelService.exportExcel([
			{ data: this.chartsData.consumed_energy.map((row) => [row[0], row[1]]), header: consumedHeaders, sheetName: this.translate.instant('iotemeter_site_energy_report.daily_energy_usage') },
			{ data: this.chartsData.demand_energy.map((row) => [row[0], row[1]]), header: demandHeaders, sheetName: this.translate.instant('iotemeter_site_energy_report.daily_power_demand') },
		], `Daily Consumption Chart.xlsx`);
	}

	ngOnDestroy() {
		this.siteSubscription?.unsubscribe();
		this.getIotemeterDailyEnergyReportsSubscription?.unsubscribe();
	}
}
